/**
 * Shared between Organization and Staff views.
 * Not shared with Provider views (Provider Connections is a separate implementation).
 */

import { useEffect, useRef, useState } from 'react';
import type { ConnectedFacility, Status } from '../../../../../js/api';
import { StatusCategoryKinds, WorkflowScopes } from '../../../constants/workflows';
import { useWorkflowMetadata, WorkflowMetadata } from '../../../hooks/useWorkflowMetadata';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { getQueueNameForStatus } from '../../../utils/workflowUtils';

type ConnectionQuickDetailsProps = {
  connection: ConnectedFacility;
  linkContext?: 'organization' | 'staff';
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export function ConnectionQuickDetails({
  connection,
  linkContext = 'organization',
}: ConnectionQuickDetailsProps) {
  const facilityName = connection.facility?.name;
  const facilityId = connection.facility?.id;
  const location = getFacilityLocationDisplayValue(connection.facility);
  const specialtyText =
    connection.opportunity?.specialty_display ||
    (connection.provider?.specialties || []).join(', ') ||
    '--';

  // Detect if the facility name would truncate; if so, stack details vertically
  const facilityNameRef = useRef<HTMLSpanElement>(null);
  const [stackVertical, setStackVertical] = useState(false);

  useEffect(() => {
    let resizeTimer: number | null = null;

    const checkTruncate = () => {
      const el = facilityNameRef.current;
      if (!el) return;
      const isTruncating = el.scrollWidth > el.clientWidth; // ellipsis condition
      // Reflect current measurement – allows un-stacking when space returns
      setStackVertical(isTruncating);
    };

    // Debounced resize handler to avoid rapid flip-flopping while dragging
    const onResize = () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(checkTruncate, 120);
    };

    // Initial check after mount/updates (use rAF to ensure DOM is painted)
    const raf = window.requestAnimationFrame(checkTruncate);
    // Re-check on window resize
    window.addEventListener('resize', onResize);
    return () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [facilityName]);

  // Clearance status
  const { statuses: clearanceStatuses } = useWorkflowMetadata(WorkflowScopes.Clearance);
  const clearanceStatus: Status | null = connection.clearance_current_status_id
    ? (clearanceStatuses?.[connection.clearance_current_status_id] as unknown as Status) || null
    : null;
  const clearanceQueue =
    getQueueNameForStatus(
      clearanceStatuses as WorkflowMetadata,
      connection.clearance_current_status_id
    ) || 'all';

  const facilitiesBasePath =
    linkContext === 'staff' ? '/staff/facilities' : '/app/organization/facilities';
  const clearanceBasePath =
    linkContext === 'staff' ? '/staff/clearance' : '/app/organization/clearance';

  return (
    <div className="tw:bg-brand-green-3 tw:rounded-lg tw:p-4 tw:text-zinc-700 tw:leading-tight">
      <div
        className={`tw:grid ${
          stackVertical ? 'tw:grid-cols-1 tw:items-start' : 'tw:grid-cols-2 tw:items-center'
        } tw:gap-x-2 tw:gap-y-2`}
      >
        {stackVertical ? (
          <>
            {/* Facility Name */}
            <div className="tw:flex tw:items-center tw:gap-2">
              <i className="fa-solid fa-hospital tw:text-brand-green-0 tw:shrink-0 fa-fw" />
              <span ref={facilityNameRef} className={stackVertical ? '' : 'tw:truncate'}>
                {facilityName || '--'}
              </span>
              {facilityId && (
                <a
                  href={`${facilitiesBasePath}?id=${facilityId}`}
                  className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:shrink-0 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
                  aria-label="View facility details"
                >
                  <i className="fa-solid fa-arrow-up-right-from-square" />
                </a>
              )}
            </div>

            {/* Location - directly under Facility when stacked */}
            <div className="tw:flex tw:items-center tw:gap-2">
              <i className="fa-solid fa-location-dot tw:text-brand-green-0 tw:shrink-0 fa-fw" />
              <span className="tw:truncate">{location || '--'}</span>
            </div>

            {/* Specialty */}
            <div className="tw:flex tw:items-center tw:gap-2">
              <i className="fa-solid fa-stethoscope tw:text-brand-green-0 tw:shrink-0 fa-fw" />
              <span className="tw:truncate">{specialtyText}</span>
            </div>

            {/* Clearance Status */}
            <div className="tw:flex tw:items-center tw:gap-2">
              <i className="fa-solid fa-check-double tw:text-brand-green-0 tw:shrink-0 fa-fw" />
              {clearanceStatus ? (
                <>
                  <span>{clearanceStatus.title}</span>
                  {clearanceStatus.category_kind !== StatusCategoryKinds.FINAL && (
                    <a
                      href={`${clearanceBasePath}?queue=${clearanceQueue}&id=${connection.clearance_id}`}
                      className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:shrink-0 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
                      aria-label="View clearance details"
                    >
                      <i className="fa-solid fa-arrow-up-right-from-square" />
                    </a>
                  )}
                </>
              ) : (
                <span className="tw:text-zinc-400">--</span>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Facility Name */}
            <div className="tw:flex tw:items-center tw:gap-2">
              <i className="fa-solid fa-hospital tw:text-brand-green-0 tw:shrink-0 fa-fw" />
              <span ref={facilityNameRef} className={stackVertical ? '' : 'tw:truncate'}>
                {facilityName || '--'}
              </span>
              {facilityId && (
                <a
                  href={`${facilitiesBasePath}?id=${facilityId}`}
                  className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:shrink-0 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
                  aria-label="View facility details"
                >
                  <i className="fa-solid fa-arrow-up-right-from-square" />
                </a>
              )}
            </div>

            {/* Specialty */}
            <div className="tw:flex tw:items-center tw:gap-2">
              <i className="fa-solid fa-stethoscope tw:text-brand-green-0 tw:shrink-0 fa-fw" />
              <span className="tw:truncate">{specialtyText}</span>
            </div>

            {/* Location */}
            <div className="tw:flex tw:items-center tw:gap-2">
              <i className="fa-solid fa-location-dot tw:text-brand-green-0 tw:shrink-0 fa-fw" />
              <span className="tw:truncate">{location || '--'}</span>
            </div>

            {/* Clearance Status */}
            <div className="tw:flex tw:items-center tw:gap-2">
              <i className="fa-solid fa-check-double tw:text-brand-green-0 tw:shrink-0 fa-fw" />
              {clearanceStatus ? (
                <>
                  <span>{clearanceStatus.title}</span>
                  {clearanceStatus.category_kind !== StatusCategoryKinds.FINAL && (
                    <a
                      href={`${clearanceBasePath}?id=${connection.clearance_id}`}
                      className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:shrink-0 tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
                      aria-label="View clearance details"
                    >
                      <i className="fa-solid fa-arrow-up-right-from-square" />
                    </a>
                  )}
                </>
              ) : (
                <span className="tw:text-zinc-400">--</span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
